/**
 * Project Milestones — every gate on the programme: register gates plus the
 * macro schedule's own milestone activities. Theme-aware and interactive.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Flag, Loader2, Search, Layers } from 'lucide-react';
import api from '@/lib/api';
import { usePanelTheme } from './panelTheme';
import { ChevronRight } from 'lucide-react';
import ModuleDrilldownDrawer from './ModuleDrilldownDrawer';

/* WBS → activity tree: replaces the flat WBS-id list with a drilldownable
   hierarchy so a gate can be traced to the activities underneath it. */
const WbsNode = ({ node, T, depth = 0, selected, onSelect, autoOpen }) => {
  const [open, setOpen] = useState(depth < 1);
  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);
  const isSel = selected && (selected === node.wbsCode || selected === node.name);
  const kids = node.children || [];
  const tone = (node.slipDays || 0) >= 7 ? T.bad : (node.slipDays || 0) > 0 ? T.warn : T.good;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] py-1 rounded-md cursor-pointer"
           onClick={() => onSelect?.(node)}
           style={{ paddingLeft: depth * 12,
                    background: isSel ? `${T.accent}1A` : 'transparent',
                    boxShadow: isSel ? `inset 2px 0 0 ${T.accent}` : 'none' }}
           onMouseEnter={(e) => { e.currentTarget.style.background = T.hover; }}
           onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
           data-testid={`wbs-node-${node.wbsCode || node.id}`}>
        {kids.length > 0 ? (
          <button type="button" onClick={() => setOpen((v) => !v)} className="p-0.5 shrink-0"
                  data-testid={`wbs-toggle-${node.wbsCode || node.id}`}>
            <ChevronRight className="w-3 h-3 transition-transform"
              style={{ color: T.muted, transform: open ? 'rotate(90deg)' : 'none' }} />
          </button>
        ) : <span className="w-4 shrink-0" />}
        {node.isMilestone && <span className="w-1.5 h-1.5 rotate-45 shrink-0" style={{ background: tone }} />}
        <span className="flex-1 truncate" style={{ color: T.text, fontWeight: kids.length ? 600 : 400 }}
              title={node.name}>{node.name}</span>
        {node.wbsCode && <span className="font-mono text-[9px]" style={{ color: T.muted }}>{node.wbsCode}</span>}
        <span className="font-mono text-[10px] w-12 text-right" style={{ color: T.text2 }}>{node.percentComplete}%</span>
        <span className="font-mono text-[10px] w-14 text-right"
              style={{ color: (node.slipDays || 0) > 0 ? T.bad : T.good }}>
          {(node.slipDays || 0) > 0 ? `+${node.slipDays}d` : 'on time'}
        </span>
      </div>
      {kids.length > 0 && (
        <div className="overflow-hidden" style={{ maxHeight: open ? 4000 : 0, transition: 'max-height 0.35s ease' }}>
          <div className="h-1 mb-1 rounded-full overflow-hidden" style={{ background: T.inner, marginLeft: depth * 12 + 20 }}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, node.percentComplete)}%`, background: tone }} />
          </div>
          {kids.map((k) => (
            <WbsNode key={k.id} node={k} T={T} depth={depth + 1} selected={selected}
                     onSelect={onSelect} autoOpen={autoOpen} />
          ))}
        </div>
      )}
    </div>
  );
};

/* Prune the tree to the branch a milestone points at, so clicking a gate
   filters WBS & Activities instead of merely highlighting a row. */
const pruneTree = (nodes, code) => {
  if (!code) return nodes;
  const out = [];
  for (const n of nodes || []) {
    const self = n.wbsCode === code || n.name === code
      || (n.name || '').replace(/ Complete$/, '') === code;
    if (self) { out.push(n); continue; }
    const kids = pruneTree(n.children, code);
    if (kids.length) out.push({ ...n, children: kids });
  }
  return out;
};

const WbsTree = ({ projectId, T, selected, onSelect }) => {
  const [tree, setTree] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    api.get(`/v1/projects/${projectId}/overview/wbs-tree`)
      .then((r) => { if (alive) setTree(r.data); })
      .catch(() => { if (alive) setTree(null); });
    return () => { alive = false; };
  }, [projectId]);
  if (!tree || (tree.tree || []).length === 0) return null;
  const pruned = pruneTree(tree.tree, selected);
  const visible = pruned.length ? pruned : tree.tree;
  return (
    <div data-testid="wbs-tree">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider" style={{ color: T.muted }}>
          WBS &amp; activities · drill down
        </span>
        {selected ? (
          <button type="button" onClick={() => onSelect?.({ wbsCode: null })}
            className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: `${T.accent}18`, color: T.accent, border: `1px solid ${T.accent}44` }}
            data-testid="wbs-clear-filter">
            Filtered to {selected} · clear
          </button>
        ) : (
          <span className="text-[10px] font-mono" style={{ color: T.muted }}>{tree.scheduleName}</span>
        )}
      </div>
      <div className="max-h-[300px] overflow-y-auto pr-1">
        {visible.map((n) => (
          <WbsNode key={n.id} node={n} T={T} selected={selected} onSelect={onSelect} autoOpen={!!selected} />
        ))}
      </div>
    </div>
  );
};

const HEALTH = [
  ['COMPLETED', 'Completed', 'good'],
  ['ON_TRACK',  'On Track',  'accent'],
  ['AT_RISK',   'At Risk',   'warn'],
  ['SLIPPING',  'Slipping',  'bad'],
  ['OVERDUE',   'Overdue',   'bad'],
];

export const ProjectMilestonesPanel = ({ projectId }) => {
  const T = usePanelTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [q, setQ] = useState('');
  const [selectedWbs, setSelectedWbs] = useState(null);
  const [drill, setDrill] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/overview/milestones`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  const rows = useMemo(() => {
    const all = data?.milestones || [];
    const ql = q.trim().toLowerCase();
    return all.filter((m) => (filter === 'ALL' || m.health === filter)
      && (!ql || (m.name || '').toLowerCase().includes(ql) || (m.phase || '').toLowerCase().includes(ql)));
  }, [data, filter, q]);

  const tone = (h) => T[(HEALTH.find((x) => x[0] === h) || [, , 'muted'])[2]] || T.muted;

  return (
    <div className="rounded-2xl overflow-hidden" data-testid="project-milestones-panel"
         style={{ background: T.card, border: `1px solid ${T.border}`,
                  boxShadow: T.isDark ? 'none' : '0 1px 2px rgba(15,23,42,0.04)' }}>
      <div className="px-5 pt-4 pb-3" style={{ background: T.sheen, borderBottom: `1px solid ${T.border}` }}>
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg grid place-items-center shrink-0"
                style={{ background: `${T.accent}1F` }}>
            <Flag className="w-3.5 h-3.5" style={{ color: T.accent }} />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold leading-tight" style={{ color: T.text }}>Project Milestones</h3>
            <p className="text-[10px]" style={{ color: T.muted }}>
              {data?.total ?? 0} gates · worst slip {data?.worstSlipDays ?? 0}d · avg {data?.avgSlipDays ?? 0}d
            </p>
          </div>
          <button type="button" onClick={() => setDrill(true)}
            className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-transform hover:-translate-y-0.5"
            style={{ background: `${T.accent}18`, color: T.accent, border: `1px solid ${T.accent}44` }}
            data-testid="milestones-drilldown-btn">
            <Layers className="w-3 h-3" /> L2 · L3
          </button>
          <div className="flex items-center gap-1.5 rounded-lg px-2 py-1"
               style={{ background: T.inner, border: `1px solid ${T.border}` }}>
            <Search className="w-3 h-3" style={{ color: T.muted }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a gate"
                   className="bg-transparent outline-none text-[11px] w-24"
                   style={{ color: T.text }} data-testid="milestone-search" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center gap-2 text-xs" style={{ color: T.muted }}
             data-testid="milestones-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading milestones…
        </div>
      ) : !data || data.total === 0 ? (
        <div className="h-48 flex items-center justify-center text-xs" style={{ color: T.muted }}
             data-testid="milestones-empty">No milestones found for this project.</div>
      ) : (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-5 gap-2" data-testid="milestones-counts">
            {HEALTH.map(([key, label, toneKey], i) => {
              const active = filter === key;
              const c = T[toneKey];
              return (
                <motion.button key={key} type="button"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => setFilter(active ? 'ALL' : key)}
                  className="rounded-xl px-2 py-2 text-center transition-all duration-200 hover:-translate-y-0.5"
                  style={{ background: active ? `${c}18` : T.inner,
                           border: `1px solid ${active ? c : T.border}` }}
                  data-testid={`milestone-count-${key.toLowerCase()}`}>
                  <div className="text-lg font-bold font-mono leading-none" style={{ color: c }}>
                    {data.counts?.[key] ?? 0}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider mt-1" style={{ color: T.muted }}>{label}</div>
                </motion.button>
              );
            })}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-wider" style={{ color: T.muted }}>
                {filter === 'ALL' ? 'All gates' : `${filter.replace('_', ' ')} gates`}
              </span>
              <span className="text-[10px] font-mono" style={{ color: T.muted }}>{rows.length} shown</span>
            </div>
            <div className="max-h-[260px] overflow-y-auto pr-1 space-y-0.5" data-testid="milestones-list">
              {rows.length === 0 ? (
                <div className="text-[11px] py-4 text-center" style={{ color: T.muted }}>No gate matches that filter.</div>
              ) : rows.map((m, i) => (
                <motion.div key={`${m.milestoneId}-${i}`}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i, 12) * 0.02 }}
                  className="flex items-center gap-2 text-[11px] py-1.5 px-2 rounded-lg transition-colors cursor-pointer"
                  onClick={() => setSelectedWbs(m.wbsCode || m.name?.replace(/ Complete$/, ''))}
                  style={{ borderBottom: `1px solid ${T.border}`,
                           background: (selectedWbs && (selectedWbs === m.wbsCode
                             || selectedWbs === m.name?.replace(/ Complete$/, ''))) ? `${T.accent}14` : 'transparent' }}
                  data-testid={`milestone-row-${m.milestoneId}`}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tone(m.health) }} />
                  <span className="flex-1 truncate" style={{ color: T.text }} title={m.name}>{m.name}</span>
                  <span className="hidden md:inline text-[10px] truncate max-w-[110px]" style={{ color: T.muted }}>{m.phase}</span>
                  <span className="font-mono text-[10px]" style={{ color: T.text2 }}>{m.forecastDate || '—'}</span>
                  <span className="font-mono font-semibold w-16 text-right"
                        style={{ color: (m.slipDays || 0) > 0 ? T.bad : T.good }}>
                    {(m.slipDays || 0) > 0 ? `+${m.slipDays}d` : 'on time'}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          <WbsTree projectId={projectId} T={T} selected={selectedWbs}
                   onSelect={(n) => setSelectedWbs(n.wbsCode || n.name || null)} />
        </div>
      )}
      {drill && (
        <ModuleDrilldownDrawer projectId={projectId} module="milestones" onClose={() => setDrill(false)} />
      )}
    </div>
  );
};

export default ProjectMilestonesPanel;
